import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";
import { useCustomer } from "./CustomerContext";
import {
  COMPARISON_LIMIT,
  getComparisonProductId,
} from "../utils/comparisonProducts";

const ComparisonContext = createContext();
const GUEST_COMPARISON_KEY = "guestComparisonProductIds";

const normalizeIds = (value) => [
  ...new Set(
    (Array.isArray(value) ? value : [])
      .map(getComparisonProductId)
      .filter(Boolean),
  ),
];

const readGuestIds = () => {
  try {
    return normalizeIds(JSON.parse(localStorage.getItem(GUEST_COMPARISON_KEY)));
  } catch {
    return [];
  }
};

const comparisonFromPayload = (payload) =>
  Array.isArray(payload?.comparison) ? payload.comparison : [];

export const useComparison = () => useContext(ComparisonContext);

export const ComparisonProvider = ({ children }) => {
  const { customer } = useCustomer();
  const customerId = customer?._id || customer?.id || null;
  const isAuthenticated = Boolean(customerId);
  const [guestIds, setGuestIds] = useState(readGuestIds);
  const [userProducts, setUserProducts] = useState([]);
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    localStorage.setItem(GUEST_COMPARISON_KEY, JSON.stringify(guestIds));
  }, [guestIds]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => setNotice(""), 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    let cancelled = false;

    const loadAndSync = async () => {
      if (!customerId) {
        setUserProducts([]);
        setIsComparisonLoading(false);
        return;
      }

      setIsComparisonLoading(true);
      try {
        const response = guestIds.length
          ? await axiosInstance.post("/customers/me/comparison/sync", {
              productIds: guestIds,
            })
          : await axiosInstance.get("/customers/me/comparison");
        if (cancelled) return;
        setUserProducts(comparisonFromPayload(response.data));
        if (guestIds.length) setGuestIds([]);
      } catch (error) {
        if (cancelled) return;
        const serverComparison = comparisonFromPayload(error.response?.data);
        setUserProducts(serverComparison);
        setNotice(
          error.response?.data?.message ||
            "Не вдалося синхронізувати список порівняння",
        );
      } finally {
        if (!cancelled) setIsComparisonLoading(false);
      }
    };

    loadAndSync();
    return () => {
      cancelled = true;
    };
  }, [customerId, guestIds]);

  const comparisonIds = isAuthenticated
    ? normalizeIds(userProducts)
    : guestIds;

  const isInComparison = useCallback(
    (productOrId) => comparisonIds.includes(getComparisonProductId(productOrId)),
    [comparisonIds],
  );

  const addToComparison = useCallback(
    async (product) => {
      const productId = getComparisonProductId(product);
      if (!productId || comparisonIds.includes(productId)) return true;

      if (comparisonIds.length >= COMPARISON_LIMIT) {
        setNotice(`До порівняння можна додати не більше ${COMPARISON_LIMIT} товарів`);
        return false;
      }

      if (!isAuthenticated) {
        setGuestIds((current) => [...current, productId]);
        setNotice("Товар додано до порівняння");
        return true;
      }

      try {
        const { data } = await axiosInstance.post("/customers/me/comparison", {
          productId,
        });
        setUserProducts(comparisonFromPayload(data));
        setNotice("Товар додано до порівняння");
        return true;
      } catch (error) {
        setNotice(error.response?.data?.message || "Не вдалося додати товар");
        return false;
      }
    },
    [comparisonIds, isAuthenticated],
  );

  const removeFromComparison = useCallback(
    async (productOrId) => {
      const productId = getComparisonProductId(productOrId);
      if (!productId) return;

      if (!isAuthenticated) {
        setGuestIds((current) => current.filter((id) => id !== productId));
        return;
      }

      try {
        const { data } = await axiosInstance.delete(
          `/customers/me/comparison/${encodeURIComponent(productId)}`,
        );
        setUserProducts(comparisonFromPayload(data));
      } catch (error) {
        setNotice(error.response?.data?.message || "Не вдалося видалити товар");
      }
    },
    [isAuthenticated],
  );

  const toggleComparison = useCallback(
    (product) =>
      isInComparison(product)
        ? removeFromComparison(product)
        : addToComparison(product),
    [addToComparison, isInComparison, removeFromComparison],
  );

  const clearComparison = useCallback(async () => {
    if (!isAuthenticated) {
      setGuestIds([]);
      return;
    }
    try {
      await axiosInstance.delete("/customers/me/comparison");
      setUserProducts([]);
    } catch (error) {
      setNotice(error.response?.data?.message || "Не вдалося очистити список");
    }
  }, [isAuthenticated]);

  const removeUnavailableItems = useCallback(
    (availableIds) => {
      if (isAuthenticated) return;
      const valid = new Set(normalizeIds(availableIds));
      setGuestIds((current) => current.filter((id) => valid.has(id)));
    },
    [isAuthenticated],
  );

  const value = useMemo(
    () => ({
      comparisonIds,
      comparisonCount: comparisonIds.length,
      isComparisonLoading,
      limit: COMPARISON_LIMIT,
      notice,
      setNotice,
      isInComparison,
      addToComparison,
      removeFromComparison,
      toggleComparison,
      clearComparison,
      removeUnavailableItems,
    }),
    [
      comparisonIds,
      isComparisonLoading,
      notice,
      isInComparison,
      addToComparison,
      removeFromComparison,
      toggleComparison,
      clearComparison,
      removeUnavailableItems,
    ],
  );

  return (
    <ComparisonContext.Provider value={value}>
      {children}
      {notice && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            right: "16px",
            bottom: "18px",
            zIndex: 1200,
            maxWidth: "min(360px, calc(100vw - 32px))",
            padding: "12px 16px",
            borderRadius: "12px",
            background: "#111827",
            color: "#fff",
            boxShadow: "0 12px 30px rgba(17, 24, 39, .25)",
          }}
        >
          {notice}
        </div>
      )}
    </ComparisonContext.Provider>
  );
};
